import { LocalStorage } from "./storage";
import { HOUR_DURATION_MS } from "./constants";
import { Logger } from "./logger/logger";
export const scServiceWorkerHistory = "__sc_service_worker_history";

const log = new Logger("serviceWorkerManager");

const hoursInMs = (numberOfHours: number): number =>
  numberOfHours * HOUR_DURATION_MS;
const ServiceWorkerUpdateCheckFrequency = hoursInMs(1);

export const MAX_HISTORY_ITEMS = 50;
interface HistoryEvent {
  type: string;
  data?: any /* eslint-disable-line @typescript-eslint/no-explicit-any */;
  timestamp?: Date;
}

const isSupported = "serviceWorker" in window.navigator;

export class ServiceWorkerManager {
  private static _instance: ServiceWorkerManager;
  reloading = false;
  updateIntervalRef: number;
  history: HistoryEvent[] = [];
  registration: ServiceWorkerRegistration | null = null;

  constructor() {
    this.handleControllerChange = this.handleControllerChange.bind(this);
    this.handleUpdateFound = this.handleUpdateFound.bind(this);
    this.update = this.update.bind(this);

    if (isSupported) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        this.handleControllerChange
      );
    }

    this.initHistory();

    this.updateIntervalRef = window.setInterval(
      this.update,
      ServiceWorkerUpdateCheckFrequency
    );
  }

  // TODO: this is not gonna be accurate if several tabs are open. Move logging to active service worker?
  // We only need this to understand/debug how service workers work. Can remove after we are satisfied with the workflow
  initHistory(): void {
    try {
      const history = LocalStorage.getInstance().getItem(
        scServiceWorkerHistory
      );
      if (Array.isArray(history)) {
        this.history = history;
      } else {
        LocalStorage.getInstance().removeItem(scServiceWorkerHistory);
      }
    } catch (err) {
      log.error("History init error:", { error: JSON.stringify(err) });
    }
  }
  getHistory(): HistoryEvent[] {
    return this.history;
  }

  addEventToHistory(event: HistoryEvent): void {
    try {
      this.history.push({ ...event, timestamp: new Date() });
      this.history = this.history.slice(
        Math.max(this.history.length - MAX_HISTORY_ITEMS, 0)
      );
      LocalStorage.getInstance().setItem(scServiceWorkerHistory, this.history);
    } catch (error) {
      log.warn(`Unable to add history event: ${{ ...event }}`, {
        error: JSON.stringify(error),
      });
    }
  }

  static getInstance(): ServiceWorkerManager {
    if (!this._instance) {
      this._instance = new ServiceWorkerManager();
    }

    return this._instance;
  }

  reload(): void {
    if (this.reloading) return; // make sure Update on Reload does not cause a loop
    this.reloading = true;
    log.info("Reloading page!", {});
    window.location.reload();
  }

  handleControllerChange(): void {
    this.addEventToHistory({ type: "controllerchange" });
    // This fires when the service worker controlling this page
    // changes, eg a new worker has skipped waiting and become
    // the new active worker.
    log.debug("Service worker controller changed", {});
    this.reload();
  }

  handleUpdateFound(): void {
    this.addEventToHistory({ type: "updatefound" });
    // A new service worker has appeared in reg.installing!
    // registration.installing: the installing worker, or undefined
    // registration.waiting: the waiting worker, or undefined
    // registration.active: the active worker, or undefined
    const newWorker = this.registration?.installing; // There is a registration and service worker at this state for sure
    if (!newWorker) return;

    newWorker.addEventListener("statechange", () =>
      this.handleServiceWorkerStateChange(newWorker)
    );
  }

  handleServiceWorkerStateChange(serviceWorker: ServiceWorker): void {
    this.addEventToHistory({ type: "statechange", data: serviceWorker.state });
    // 'installing' - the install event has fired, but not yet complete
    // 'installed'  - install complete
    // 'activating' - the activate event has fired, but not yet complete
    // 'activated'  - fully active
    // 'redundant'  - discarded. Either failed install, or it's been
    //                replaced by a newer version
    log.debug("Service worker state changed", {
      state: JSON.stringify(serviceWorker.state),
    });

    if (serviceWorker.state === "installed") {
      // The new service worker is installed and ready to be activated.
      this.handleServiceWorkerInstallation(serviceWorker);
    }
  }

  // This function decides what to do with the new service worker that is installed already
  handleServiceWorkerInstallation(serviceWorker: ServiceWorker): void {
    // Just an example about how to show user/developer that the service worker
    // is gonna refresh the page to activate the latest service worker
    if (process.env.REACT_APP_SC_ENV === "development") {
      const div = document.createElement("div");
      div.style.position = "fixed";
      div.style.bottom = "32px";
      div.style.right = "10px";
      div.style.zIndex = "10000";
      div.style.background = "white";
      div.style.border = "1px solid red";
      div.style.color = "black";
      div.innerHTML = "REFRESHING IN 5 SECONDS";
      div.id = "service-worker-installation";
      document.body.appendChild(div);
    }

    setTimeout(() => {
      // back up reload after 1min if controllerchange event doesn't come back
      this.addEventToHistory({
        type: "force-reload",
        data: serviceWorker.state,
      });

      this.reload();
    }, 60 * 1000);

    setTimeout(() => {
      this.activateServiceWorker(serviceWorker);
    }, 5000);
  }

  activateServiceWorker(serviceWorker: ServiceWorker): void {
    // Service worker is gonna go to activated mode after this and when the tab is reloaded via handleControllerChange
    // this client is gonna be controlled by this new service worker.
    try {
      serviceWorker.postMessage({ type: "skipWaiting" });
    } catch (error) {
      log.warn("activate failed", { error: JSON.stringify(error) });
    }
  }

  // ignoreSupportCheck is an argument for the testing purpose
  async register(ignoreSupportCheck?: boolean): Promise<void> {
    if (ignoreSupportCheck || isSupported) {
      // The URL constructor is available in all browsers that support SW.
      const publicUrl = new URL(
        (process as { env: { [key: string]: string } }).env.PUBLIC_URL,
        window.location.href
      );

      if (publicUrl.origin !== window.location.origin) {
        // Our service worker won't work if PUBLIC_URL is on a different origin
        // from what our page is served on. This might happen if a CDN is used to
        // serve assets; see https://github.com/facebook/create-react-app/issues/2374
        return;
      }

      const swUrl = `${process.env.PUBLIC_URL}/serviceworker.js`;
      try {
        this.registration = await navigator.serviceWorker
          .register(swUrl)
          .catch((err) => {
            log.error("ServiceWorker registration failed: ", {
              errorMessage: err.message,
            });
            return null;
          });

        if (!this.registration) return;

        log.debug("ServiceWorker registration successful with scope: ", {
          registrationscope: this.registration.scope,
        });
        this.registration.addEventListener(
          "updatefound",
          this.handleUpdateFound
        );
      } catch (error) {
        log.error("ServiceWorker unable to register:", {
          error: JSON.stringify(error),
        });
      }
    } else {
      return;
    }
  }
  // ignoreSupportCheck is an argument for the testing purpose
  async unregister(ignoreSupportCheck?: boolean): Promise<void> {
    if (ignoreSupportCheck || isSupported) {
      const registration = await navigator.serviceWorker.ready; // ready promise never rejects, waits until service worker is active
      if (registration) {
        return registration
          .unregister()
          .then((done) => {
            if (done) {
              this.registration = null;
              log.info("ServiceWorker unregistered successfully", {});
              return;
            }
            throw new Error("false"); // Unregistration may fail even if the promise is successful
          })
          .catch((err) => {
            log.error("ServiceWorker unregisteration failed:", { error: err });
          });
      } else {
        log.warn(
          "ServiceWorkerRegistration associated with the current page NOT has an active worker.",
          {}
        );
        return;
      }
    } else {
      return;
    }
  }

  update(): void {
    log.info("Checking service worker update", {});
    try {
      this.registration?.update();
    } catch (error) {
      // handled error so sentry not capture this
      log.error("registration update error", { error: JSON.stringify(error) });
    }
  }

  stop(): void {
    clearInterval(this.updateIntervalRef);
  }
}
